if (!customElements.get('load-more-products')) {
  class LoadMoreProducts extends HTMLElement {
    constructor() {
      super();
      this.gridId = this.dataset.gridId;
      this.sectionId = this.dataset.sectionId;
      this.grid = document.getElementById(this.gridId);

      // Event delegation: the button inside [data-load-more-pager] gets
      // replaced wholesale on every load, so we bind once on the custom
      // element itself rather than on the button directly.
      this.addEventListener('click', this.handleClick.bind(this));

      // Back/forward: re-fetch and swap in just that page's content rather
      // than reloading. A hard reload fights with the browser's scroll
      // restoration, which tries to restore the old (taller, appended)
      // scroll position against a shorter single-page document and ends up
      // clamping to the bottom.
      if (!window.__loadMoreProductsInstance) {
        window.__loadMoreProductsInstance = this;
        window.addEventListener('popstate', () => {
          window.__loadMoreProductsInstance.syncToCurrentUrl();
        });
      }
    }

    handleClick(event) {
      const button = event.target.closest('[data-load-more-button]');
      if (button) this.loadMore(button);
    }

    async fetchSectionHtml(url) {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    }

    async loadMore(button) {
      if (!this.grid || button.hasAttribute('disabled')) return;

      const nextUrl = button.dataset.nextUrl;
      const nextPage = button.dataset.nextPage;
      if (!nextUrl) return;

      this.setLoading(button, true);

      try {
        const html = await this.fetchSectionHtml(nextUrl);

        // Append the freshly rendered products
        const newGrid = html.getElementById(this.gridId);
        const newItems = newGrid ? Array.from(newGrid.children) : [];
        const fragment = document.createDocumentFragment();
        newItems.forEach((item) => fragment.appendChild(item));
        this.grid.appendChild(fragment);

        // Swap in the whole pager block (button + pagination) so the
        // current page, remaining count, and prev/next links stay accurate
        this.swapPager(html);

        if (nextPage) this.updateUrl(nextPage);
      } catch (error) {
        console.error(error);
        const text = button.querySelector('[data-load-more-text]');
        if (text) text.textContent = 'Something went wrong — please refresh and try again';
        button.setAttribute('disabled', 'disabled');
      } finally {
        this.setLoading(button, false);
      }
    }

    async syncToCurrentUrl() {
      if (!this.grid) return;

      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);

      this.grid.style.opacity = '0.5';

      try {
        const html = await this.fetchSectionHtml(url.toString());

        // Replace (not append) — going back should show only that page's
        // products, matching what a fresh visit to that URL would show.
        const newGrid = html.getElementById(this.gridId);
        if (newGrid) this.grid.innerHTML = newGrid.innerHTML;

        this.swapPager(html);

        this.grid.scrollIntoView({ block: 'start' });
      } catch (error) {
        console.error(error);
        // Fall back to a real navigation only if the AJAX sync itself fails
        window.location.reload();
      } finally {
        this.grid.style.opacity = '';
      }
    }

    swapPager(html) {
      const newPager = html.querySelector('load-more-products [data-load-more-pager]');
      const pager = this.querySelector('[data-load-more-pager]');
      if (newPager && pager) pager.innerHTML = newPager.innerHTML;
    }

    updateUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', page);
      window.history.pushState({ page: page }, '', url);
    }

    setLoading(button, isLoading) {
      if (!button) return;
      button.toggleAttribute('disabled', isLoading);
      const spinner = button.querySelector('[data-load-more-spinner]');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }
  }

  customElements.define('load-more-products', LoadMoreProducts);
}
